import { api } from './client';

export type ContractStatus = 'active' | 'expiring_soon' | 'expired' | 'inactive';

export interface Organization {
  id: string;
  name: string;
  inn: string;
  kpp: string | null;
  legal_address: string | null;
  contact_person: string | null;
  contact_position: string | null;
  contact_phone: string | null;
  contact_phone_ext: string | null;
  contact_email: string | null;
  contact_telegram: string | null;
  contact_max: string | null;
  telegram_chat_url: string | null;
  max_chat_url: string | null;
  bitrix_deal_url: string | null;
  website: string | null;
  domains: string | null;
  notes: string | null;
  contract_active: boolean;
  contract_no: string | null;
  contract_started_at: string | null;
  contract_ends_at: string | null;
  tariff: string | null;
  contract_status: ContractStatus;
  contract_days_left: number | null;
  project_status: 'support' | 'pilot' | 'no_support';
  sla_plan_id: string | null;
  created_at: string;
  updated_at: string;
  // Производные счётчики (заполняются list-endpoint'ом).
  clients_count?: number;
  tickets_open_count?: number;
  tickets_closed_count?: number;
  last_ticket_at?: string | null;
  is_hidden?: boolean;
  // Партнёр (v0.9.41) — источник лида / исполнитель работ. Часто из BC.Pilot.
  partner_org?: string | null;
  partner_inn?: string | null;
  partner_contact_name?: string | null;
  partner_contact_phone?: string | null;
  partner_contact_email?: string | null;
  // Структурные данные (v0.9.58) из B24 + ФНС
  industry?: string | null;
  employees?: number | null;
  okved?: string | null;
  ogrn?: string | null;
  director?: string | null;
}

export interface OrganizationIn {
  name: string;
  inn: string;
  kpp?: string;
  legal_address?: string;
  contact_person?: string;
  contact_position?: string;
  contact_phone?: string;
  contact_phone_ext?: string;
  contact_email?: string;
  contact_telegram?: string;
  contact_max?: string;
  telegram_chat_url?: string;
  max_chat_url?: string;
  bitrix_deal_url?: string;
  notes?: string;
  contract_active?: boolean;
  contract_no?: string;
  contract_started_at?: string | null;
  contract_ends_at?: string | null;
  tariff?: string;
  project_status?: 'support' | 'pilot' | 'no_support';
  sla_plan_id?: string | null;
  partner_org?: string | null;
  partner_inn?: string | null;
  partner_contact_name?: string | null;
  partner_contact_phone?: string | null;
  partner_contact_email?: string | null;
}

export interface InstallationVersion {
  id: string;
  installation_id: string;
  component: string;
  installed_at: string;
  base: string | null;
  current: string | null;
  branch: string | null;
  commit_sha: string | null;
  commit_title: string | null;
  raw_text: string | null;
  note: string | null;
  created_at: string;
}

export type ProjectStatus = 'support' | 'pilot' | 'no_support';

export interface Installation {
  id: string;
  organization_id: string;
  name: string;
  product: string;
  version: string | null;
  host_url: string | null;
  notes: string | null;
  support_override: ProjectStatus | null;
  deployed_at: string | null;
  last_checked_at: string | null;
  created_at: string;
  updated_at: string;
  // Анкета инсталляции — все ключи хранят value из справочника OptionItem.
  installation_type: string | null;
  os: string | null;
  office_package: string | null;
  dlp: string | null;
  antivirus: string | null;
  sandbox: string | null;
  siem: string | null;
  storage_type: string | null;
  auth_type: string | null;
  auth_provider: string | null;
  license_users_named: number | null;
  license_users_concurrent: number | null;
  license_contractors: number | null;
  // license_options: dict {license_option.value: bool|number} — какие опции
  // лицензии включены и квоты для value_type=number.
  license_options: Record<string, boolean | number>;
  // Pilot lifecycle (v0.9.36) — заполнено, если эта инсталляция — пилот.
  pilot_started_at?: string | null;
  pilot_planned_end_at?: string | null;
  pilot_phase?: 'poc' | 'trial' | 'decision' | null;
  pilot_outcome?: 'converted' | 'cancelled' | null;
  pilot_actual_end_at?: string | null;
  pilot_extended_count?: number;
  pilot_notes?: string | null;
  // SSL мониторинг (v0.9.62)
  ssl_expires_at?: string | null;
  ssl_issuer?: string | null;
  ssl_last_check?: string | null;
  ssl_last_error?: string | null;
}

export const orgsApi = {
  list: (q?: string) => api.get<Organization[]>('/api/v1/organizations', { params: { q } }).then(r => r.data),
  get:  (id: string)   => api.get<Organization>(`/api/v1/organizations/${id}`).then(r => r.data),
  create: (p: OrganizationIn) => api.post<Organization>('/api/v1/organizations', p).then(r => r.data),
  remove: (id: string) => api.delete(`/api/v1/organizations/${id}`),
  trash: () => api.get<Organization[]>('/api/v1/organizations/_trash').then(r => r.data),
  restore: (id: string) => api.post<Organization>(`/api/v1/organizations/${id}/restore`).then(r => r.data),
  purge: (id: string) => api.delete(`/api/v1/organizations/${id}/purge`),
  update: (id: string, p: Partial<OrganizationIn>) =>
    api.patch<Organization>(`/api/v1/organizations/${id}`, p).then(r => r.data),

  installations: (orgId: string) =>
    api.get<Installation[]>(`/api/v1/organizations/${orgId}/installations`).then(r => r.data),
  createInstall: (orgId: string, p: Partial<Installation>) =>
    api.post<Installation>(`/api/v1/organizations/${orgId}/installations`, p).then(r => r.data),
  updateInstall: (orgId: string, instId: string, p: Partial<Installation>) =>
    api.patch<Installation>(`/api/v1/organizations/${orgId}/installations/${instId}`, p).then(r => r.data),
  deleteInstall: (orgId: string, instId: string) =>
    api.delete(`/api/v1/organizations/${orgId}/installations/${instId}`),

  versions: (orgId: string, instId: string) =>
    api.get<InstallationVersion[]>(`/api/v1/organizations/${orgId}/installations/${instId}/versions`).then(r => r.data),
  addVersion: (orgId: string, instId: string, p: Partial<InstallationVersion>) =>
    api.post<InstallationVersion>(`/api/v1/organizations/${orgId}/installations/${instId}/versions`, p).then(r => r.data),
  deleteVersion: (orgId: string, instId: string, verId: string) =>
    api.delete(`/api/v1/organizations/${orgId}/installations/${instId}/versions/${verId}`),
};

// ── Client users (staff CRUD) ────────────────────────────────────────────────
export interface ClientUser {
  id: string;
  email: string;
  full_name: string;
  type: 'client' | 'staff';
  role: 'client' | 'agent' | 'admin';
  is_active: boolean;
  organization_id?: string | null;
  support_lines?: number[];
  /** Подписка на письма обо ВСЕХ новых тикетах своих линий (staff). */
  notify_new_tickets?: boolean;
  position?: string | null;
  phone?: string | null;
  telegram_username?: string | null;
  max_username?: string | null;
}

export interface StaffPatch {
  full_name?: string;
  email?: string;
  role?: 'agent' | 'admin';
  position?: string | null;
  phone?: string | null;
  is_active?: boolean;
  support_lines?: number[];
  notify_new_tickets?: boolean;
}

export interface ClientCreateOut extends ClientUser {
  organization_id: string | null;
  activation_url: string | null;
  activation_expires_at: string | null;
}

export const clientsApi = {
  list: (organization_id?: string) =>
    api.get<ClientUser[]>('/api/v1/auth/clients', { params: { organization_id } }).then(r => r.data),
  create: (p: { email: string; full_name?: string; password?: string; organization_id: string }) =>
    api.post<ClientCreateOut>('/api/v1/auth/clients', p).then(r => r.data),
  forgotPassword: (email: string) =>
    api.post<{ sent: boolean; activation_url: string | null }>(
      '/api/v1/auth/forgot', { email }
    ).then(r => r.data),
  activate: (token: string, password: string, pd_consent: boolean, feedback_consent: boolean) =>
    api.post<{ email: string }>('/api/v1/auth/activate',
      { token, password, pd_consent, feedback_consent }).then(r => r.data),
  activateInfo: (token: string) =>
    api.get<{ email: string; full_name: string }>(`/api/v1/auth/activate-info/${token}`).then(r => r.data),
  patch: (id: string, p: {
    full_name?: string; email?: string; is_active?: boolean;
    position?: string | null; phone?: string | null;
    telegram_username?: string | null; max_username?: string | null;
    organization_id?: string;
  }) =>
    api.patch<ClientUser>(`/api/v1/auth/clients/${id}`, p).then(r => r.data),
  inviteToTg: (orgId: string, userId: string) =>
    api.post<{ sent: boolean; channel: string; invite_link: string | null; message: string }>(
      `/api/v1/organizations/${orgId}/sd-accounts/${userId}/invite-to-tg`
    ).then(r => r.data),
  inviteToMax: (orgId: string, userId: string) =>
    api.post<{ sent: boolean; channel: string; invite_link: string | null; message: string }>(
      `/api/v1/organizations/${orgId}/sd-accounts/${userId}/invite-to-max`
    ).then(r => r.data),
  remove: (id: string) =>
    api.delete(`/api/v1/auth/clients/${id}`),
  resetPassword: (id: string) =>
    api.post<{ activation_url: string; activation_expires_at: string }>(
      `/api/v1/auth/clients/${id}/reset-password`).then(r => r.data),
};

export interface StaffCreatedOut {
  id: string; email: string; full_name: string; role: string;
  activation_url: string; activation_expires_at: string;
}

export const staffApi = {
  list: () => api.get<ClientUser[]>('/api/v1/auth/staff').then(r => r.data),
  create: (p: { email: string; full_name?: string; role?: 'agent' | 'admin'; position?: string; phone?: string }) =>
    api.post<StaffCreatedOut>('/api/v1/auth/staff', p).then(r => r.data),
  patch: (id: string, p: StaffPatch) =>
    api.patch<ClientUser>(`/api/v1/auth/staff/${id}`, p).then(r => r.data),
  resetPassword: (id: string) =>
    api.post<{ activation_url: string; activation_expires_at: string }>(
      `/api/v1/auth/staff/${id}/reset-password`).then(r => r.data),
};

export const CONTRACT_LABEL: Record<ContractStatus, string> = {
  active: 'Договор действует',
  expiring_soon: 'Истекает',
  expired: 'Договор истёк',
  inactive: 'Не активен',
};

export const CONTRACT_BADGE: Record<ContractStatus, string> = {
  active: 'bg-green-900/40 text-green-300 border-green-800',
  expiring_soon: 'bg-yellow-900/40 text-yellow-300 border-yellow-800',
  expired: 'bg-red-900/60 text-red-200 border-red-700',
  inactive: 'bg-slate-800 text-slate-500 border-slate-700',
};
